import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';

const NikeTakebackWelcomeView: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useAppContext();

  // Reset state when entering the takeback flow
  React.useEffect(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  const handleGetStarted = () => {
    navigate('/nike/before-start');
  };

  const handleBack = () => {
    navigate('/nike');
  };

  const valueProps = [
    {
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Earn Credit',
      description: 'Get up to 45% of retail value back as store credit. Nike Members earn 5% extra.',
    },
    {
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      title: 'Extend Life',
      description: 'Your shoes get cleaned, certified, and sold to someone who\'ll love them.',
    },
    {
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Reduce Waste',
      description: 'Keep shoes out of landfills. Move toward a more sustainable future together.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      {/* Header */}
      <header className="py-6 px-8 flex items-center justify-between">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-3">
          <svg className="h-6 w-auto text-white" viewBox="0 0 69 32" fill="currentColor">
            <path d="M68.56 4.05c-.4-.15-5.86-.95-9.84 2.01-3.98 2.96-17.14 17.37-17.14 17.37s-12.87-8.36-13.93-8.97c-1.06-.61-6.85-3.29-11.11 1.21-4.26 4.5-12.15 12.67-12.15 12.67s-.55.54-.08.8c.47.26 1.09-.01 1.09-.01s20.22-8.5 22.01-9.32c1.79-.82 6.13-2.51 10.18 1.06 4.05 3.57 15.89 13.32 15.89 13.32s3.21 2.72 6.23.86c3.02-1.86 9.1-6.04 9.1-6.04s.87-.69.17-1.26c-.7-.57-1.43.11-1.43.11s-7.66 4.4-9.52 5.32c-1.86.92-4.21 1.1-7.04-1.47-2.83-2.57-13.97-12.2-13.97-12.2s10.66-11.77 13.46-14.33c2.8-2.56 7.69-4.4 12.89-.47 0 0 3.54 2.69 4.25 3.17.71.48 1.62.59 1.62-.28 0-.87-.68-1.55-.68-1.55z"/>
          </svg>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <svg className="w-8 h-8 text-[#FA5400]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="text-[#FA5400] font-bold uppercase tracking-widest">Nike Refurbished</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            Trade In. Earn Credit.
          </h1>
          <p className="text-xl text-white/60 max-w-lg mx-auto">
            Give your Nike and Jordan footwear a second life while earning credit toward your next pair.
          </p>
        </motion.div>

        {/* Value Props */}
        <div className="grid grid-cols-3 gap-6 max-w-4xl w-full mb-12">
          {valueProps.map((prop, index) => (
            <motion.div
              key={prop.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
              className="bg-white/5 rounded-2xl p-6 text-center border border-white/10"
            >
              <div className="text-[#FA5400] mb-4 flex justify-center">
                {prop.icon}
              </div>
              <h3 className="text-white font-bold text-lg mb-2">{prop.title}</h3>
              <p className="text-white/60 text-sm">{prop.description}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          onClick={handleGetStarted}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="bg-white text-[#111111] px-12 py-4 rounded-full font-bold uppercase tracking-wider text-lg shadow-xl hover:bg-gray-100 transition-colors"
        >
          Get Started
        </motion.button>

        {/* Terms */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="text-white/40 text-sm mt-6"
        >
          Nike & Jordan footwear only. Subject to condition assessment.
        </motion.p>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10">
        Powered by Pentatonic AI
      </footer>
    </div>
  );
};

export default NikeTakebackWelcomeView;
